/* Submission Audio Hero Visual Runtime.
 * Decorative, full-screen WebGL profiles with one managed lifecycle. */
(function (root, factory) {
  'use strict';

  var runtime = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = runtime;
    return;
  }

  if (!root || !root.document) return;
  var installKey = typeof Symbol === 'function'
    ? Symbol.for('submission-audio.hero-visual-runtime')
    : '__saHeroVisualRuntimeInstalled';
  if (root.document[installKey]) return;
  root.document[installKey] = true;
  runtime.createHeroVisualManager({ window: root, document: root.document }).start();
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var CANVAS_SELECTOR = '[data-sa-hero-visual]';
  var DEFINITION_ATTRIBUTE = 'data-sa-hero-visual-definition';
  var FRAGMENT_ENGINE = 'fragment-shader';
  var TUBES_CURSOR_ENGINE = 'tubes-cursor';
  var TUBES_CURSOR_MODULE_URL = 'https://cdn.jsdelivr.net/npm/threejs-components@0.0.19/build/cursors/tubes1.min.js';
  var ALLOWED_PROFILES = {
    'violet-plasma-v1': FRAGMENT_ENGINE,
    'neon-flow-v1': TUBES_CURSOR_ENGINE,
    'odyssey-lightning-v1': FRAGMENT_ENGINE
  };
  var VERTEX_SOURCE = [
    'attribute vec2 a_position;',
    'void main() {',
    '  gl_Position = vec4(a_position, 0.0, 1.0);',
    '}'
  ].join('\n');

  function numberAttribute(node, name, allowBlank) {
    var raw = node.getAttribute(name);
    if (allowBlank && (raw === null || raw === '')) return null;
    var value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : NaN;
  }

  function validHexColours(value, count) {
    return Array.isArray(value) && value.length === count && value.every(function (colour) {
      return typeof colour === 'string' && /^#[0-9a-f]{6}$/i.test(colour);
    });
  }

  function readTubesOptions(definition) {
    var options;
    try {
      options = JSON.parse(definition.textContent || '');
    } catch (_error) {
      return null;
    }
    var tubes = options && options.tubes;
    var lights = tubes && tubes.lights;
    if (!tubes || !lights || !validHexColours(tubes.colors, 3) ||
        !validHexColours(lights.colors, 4) || !Number.isFinite(lights.intensity) ||
        lights.intensity <= 0) return null;
    return {
      tubes: {
        colors: tubes.colors.slice(),
        lights: {
          intensity: lights.intensity,
          colors: lights.colors.slice()
        }
      }
    };
  }

  function readProfile(canvas) {
    var id = canvas.getAttribute('data-sa-hero-visual-profile');
    var definition = canvas.nextElementSibling;
    var expectedEngine = ALLOWED_PROFILES[id];
    if (!expectedEngine || !definition ||
        !definition.hasAttribute(DEFINITION_ATTRIBUTE) ||
        definition.getAttribute('data-profile-id') !== id ||
        definition.getAttribute('data-abi-version') !== '1') return null;

    var engine = definition.getAttribute('data-engine') || FRAGMENT_ENGINE;
    if (engine !== expectedEngine) return null;
    if (engine === TUBES_CURSOR_ENGINE) {
      var tubesOptions = readTubesOptions(definition);
      if (!tubesOptions) return null;
      return {
        id: id,
        engine: engine,
        moduleUrl: TUBES_CURSOR_MODULE_URL,
        tubesOptions: tubesOptions
      };
    }

    var precision = definition.getAttribute('data-precision');
    var dprCap = numberAttribute(definition, 'data-dpr-cap', true);
    var fpsWide = numberAttribute(definition, 'data-fps-wide', false);
    var fpsNarrow = numberAttribute(definition, 'data-fps-narrow', false);
    var staticTime = numberAttribute(definition, 'data-static-time', false);
    if ((precision !== 'mediump' && precision !== 'adaptive') ||
        Number.isNaN(dprCap) || Number.isNaN(fpsWide) ||
        Number.isNaN(fpsNarrow) || Number.isNaN(staticTime)) return null;

    var fragmentSource = definition.textContent || '';
    if (!fragmentSource || fragmentSource.indexOf('__SA_FRAGMENT_PRECISION__') === -1) return null;
    return {
      id: id,
      engine: engine,
      precision: precision,
      dprCap: dprCap,
      fpsWide: fpsWide,
      fpsNarrow: fpsNarrow,
      staticTime: staticTime,
      fragmentSource: fragmentSource
    };
  }

  function createHeroVisualManager(environment) {
    var win = environment.window;
    var doc = environment.document;
    var controllers = new Set();
    var byCanvas = new WeakMap();
    var listeners = [];
    var started = false;
    var destroyed = false;
    var suspendedForBfcache = false;
    var rafId = 0;
    var externalModulePromises = new Map();
    /* No bare dynamic-import call here: the storefront CDN's minifier
       rewrites it into a CommonJS require. theme.liquid provides
       window.saHeroVisualImport from an inline module script that the
       minifier never touches. */
    var importModule = environment.importModule || function (url) {
      if (typeof win.saHeroVisualImport === 'function') return win.saHeroVisualImport(url);
      return Promise.reject(new Error('saHeroVisualImport helper is missing'));
    };

    var reduceMotion = win.matchMedia
      ? win.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };
    var narrowViewport = win.matchMedia
      ? win.matchMedia('(max-width: 700px)')
      : { matches: false };
    var finePointer = win.matchMedia
      ? win.matchMedia('(hover: hover) and (pointer: fine)')
      : { matches: false };
    var connection = win.navigator && win.navigator.connection;

    function listen(target, type, handler, options) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(type, handler, options);
      listeners.push(function () { target.removeEventListener(type, handler, options); });
    }

    function setState(canvas, state) {
      canvas.setAttribute('data-sa-hero-visual-state', state);
    }

    function hasSaveData() {
      return !!(connection && connection.saveData);
    }

    function introActive() {
      var rootElement = doc.documentElement;
      if (!rootElement || !rootElement.classList ||
          !rootElement.classList.contains('sa-hintro-on') ||
          rootElement.classList.contains('sa-hintro-force-off')) return false;
      var overlay = doc.querySelector && doc.querySelector('.sa-hintro:not(.sa-hintro--gone)');
      return !!(overlay && overlay.isConnected !== false);
    }

    function warn(controller, reason, detail) {
      if (controller.warned[reason]) return;
      controller.warned[reason] = true;
      if (win.console && win.console.warn) {
        win.console.warn('[Hero Visual] ' + reason + ' (' + controller.profileId + ')', detail || '');
      }
    }

    function compileShader(gl, type, source) {
      var shader = gl.createShader(type);
      if (!shader) throw new Error('Unable to allocate shader');
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        var message = gl.getShaderInfoLog(shader) || 'Shader compilation failed';
        gl.deleteShader(shader);
        throw new Error(message);
      }
      return shader;
    }

    function fragmentPrecision(gl, policy) {
      if (policy !== 'adaptive') return 'mediump';
      if (!gl.getShaderPrecisionFormat) return 'mediump';
      var format = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
      return format && format.precision > 0 ? 'highp' : 'mediump';
    }

    function detachPointer(controller) {
      if (!controller.pointerTarget) return;
      controller.pointerTarget.removeEventListener('pointermove', controller.onPointerMove);
      controller.pointerTarget.removeEventListener('pointerleave', controller.onPointerLeave);
      controller.pointerTarget = null;
      controller.onPointerMove = null;
      controller.onPointerLeave = null;
    }

    function detachExternalClick(controller) {
      if (!controller.externalClickTarget) return;
      controller.externalClickTarget.removeEventListener('click', controller.onExternalClick);
      controller.externalClickTarget = null;
      controller.onExternalClick = null;
    }

    /* Click-shuffle palette stays inside the brand hue band: blues (210°)
       through purples and pinks to reds (370° ≡ 10°). No greens, yellows
       or oranges. */
    var RANDOM_HUE_START = 210;
    var RANDOM_HUE_SPAN = 160;

    function hslToHex(hue, saturation, lightness) {
      var chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
      var huePrime = hue / 60;
      var secondary = chroma * (1 - Math.abs((huePrime % 2) - 1));
      var r = 0;
      var g = 0;
      var b = 0;
      if (huePrime < 1) { r = chroma; g = secondary; }
      else if (huePrime < 2) { r = secondary; g = chroma; }
      else if (huePrime < 3) { g = chroma; b = secondary; }
      else if (huePrime < 4) { g = secondary; b = chroma; }
      else if (huePrime < 5) { r = secondary; b = chroma; }
      else { r = chroma; b = secondary; }
      var base = lightness - (chroma / 2);
      return '#' + [r, g, b].map(function (channel) {
        return Math.round((channel + base) * 255).toString(16).padStart(2, '0');
      }).join('');
    }

    function randomColours(count) {
      return new Array(count).fill(null).map(function () {
        var hue = (RANDOM_HUE_START + (Math.random() * RANDOM_HUE_SPAN)) % 360;
        var saturation = 0.72 + (Math.random() * 0.28);
        var lightness = 0.5 + (Math.random() * 0.16);
        return hslToHex(hue, saturation, lightness);
      });
    }

    function disposeExternal(controller) {
      controller.externalGeneration += 1;
      controller.externalLoading = false;
      detachExternalClick(controller);
      if (controller.externalApp && typeof controller.externalApp.dispose === 'function') {
        try {
          controller.externalApp.dispose();
        } catch (error) {
          warn(controller, 'external dispose failed', error && error.message);
        }
      }
      controller.externalApp = null;
    }

    function releaseResources(controller, mayDelete) {
      detachPointer(controller);
      disposeExternal(controller);
      var gl = controller.gl;
      var resources = controller.resources;
      if (gl && resources && mayDelete && (!gl.isContextLost || !gl.isContextLost())) {
        if (resources.buffer) gl.deleteBuffer(resources.buffer);
        if (resources.program) gl.deleteProgram(resources.program);
      }
      controller.resources = null;
      controller.gl = null;
      controller.lastFrame = null;
    }

    function nearestHero(controller) {
      return controller.canvas.closest ? controller.canvas.closest('section') : null;
    }

    function attachPointer(controller) {
      detachPointer(controller);
      var resources = controller.resources;
      if (!resources || resources.pointer === null || resources.pointerStrength === null ||
          !finePointer.matches || reduceMotion.matches) return;
      var target = nearestHero(controller);
      if (!target || !target.addEventListener) return;
      controller.pointerTarget = target;
      controller.onPointerMove = function (event) {
        var rect = controller.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        controller.pointer.x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        controller.pointer.y = Math.max(0, Math.min(1, 1 - ((event.clientY - rect.top) / rect.height)));
        controller.pointer.targetStrength = 1.15;
      };
      controller.onPointerLeave = function () { controller.pointer.targetStrength = 1; };
      target.addEventListener('pointermove', controller.onPointerMove, { passive: true });
      target.addEventListener('pointerleave', controller.onPointerLeave, { passive: true });
    }

    function loadExternalModule(url) {
      if (!externalModulePromises.has(url)) {
        externalModulePromises.set(url, Promise.resolve().then(function () {
          return importModule(url);
        }));
      }
      return externalModulePromises.get(url);
    }

    function externalFallbackRequired() {
      var networkType = connection && connection.effectiveType;
      return reduceMotion.matches || narrowViewport.matches || hasSaveData() ||
        networkType === 'slow-2g' || networkType === '2g' || networkType === '3g';
    }

    function externalReady(controller) {
      return !externalFallbackRequired() && doc.readyState !== 'loading' && eligible(controller);
    }

    function initializeExternal(controller) {
      if (controller.externalApp || controller.externalLoading || controller.failed ||
          controller.lost || controller.destroyed || !externalReady(controller)) return;
      var generation = controller.externalGeneration + 1;
      controller.externalGeneration = generation;
      controller.externalLoading = true;
      setState(controller.canvas, 'loading');
      loadExternalModule(controller.profile.moduleUrl).then(function (module) {
        if (generation !== controller.externalGeneration || controller.destroyed ||
            !externalReady(controller)) return;
        var createTubesCursor = module && (module.default || module);
        if (typeof createTubesCursor !== 'function') throw new Error('TubesCursor module has no default factory');
        var app = createTubesCursor(controller.canvas, controller.profile.tubesOptions);
        if (!app || !app.tubes || typeof app.dispose !== 'function') {
          if (app && typeof app.dispose === 'function') app.dispose();
          throw new Error('TubesCursor module does not implement the managed lifecycle');
        }
        if (generation !== controller.externalGeneration || controller.destroyed) {
          app.dispose();
          return;
        }
        controller.externalApp = app;
        var target = nearestHero(controller);
        if (target && target.addEventListener) {
          controller.externalClickTarget = target;
          controller.onExternalClick = function () {
            if (!controller.externalApp || !controller.externalApp.tubes) return;
            var tubes = controller.externalApp.tubes;
            if (typeof tubes.setColors === 'function') tubes.setColors(randomColours(3));
            if (typeof tubes.setLightsColors === 'function') tubes.setLightsColors(randomColours(4));
          };
          target.addEventListener('click', controller.onExternalClick);
        }
        setState(controller.canvas, 'ready');
      }).catch(function (error) {
        if (generation !== controller.externalGeneration || controller.destroyed) return;
        controller.failed = true;
        setState(controller.canvas, 'fallback');
        warn(controller, 'external profile failed', error && error.message);
      }).finally(function () {
        if (generation === controller.externalGeneration) controller.externalLoading = false;
      });
    }

    function refreshExternalControllers() {
      controllers.forEach(function (controller) {
        if (controller.profile.engine !== TUBES_CURSOR_ENGINE) return;
        if (externalReady(controller)) {
          initializeExternal(controller);
          return;
        }
        if (controller.externalApp || controller.externalLoading) disposeExternal(controller);
        if (controller.failed || controller.lost || externalFallbackRequired()) {
          setState(controller.canvas, 'fallback');
        } else if (!controller.destroyed) {
          setState(controller.canvas, 'idle');
        }
      });
    }

    function initialize(controller) {
      if (controller.profile.engine !== FRAGMENT_ENGINE) return false;
      if (controller.resources || controller.failed || controller.lost || hasSaveData()) {
        return !!controller.resources;
      }
      var gl = controller.canvas.getContext('webgl', {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        powerPreference: 'low-power',
        preserveDrawingBuffer: false
      });
      if (!gl) {
        controller.failed = true;
        setState(controller.canvas, 'fallback');
        warn(controller, 'WebGL unavailable');
        return false;
      }

      var vertex = null;
      var fragment = null;
      var program = null;
      var buffer = null;
      try {
        var precision = fragmentPrecision(gl, controller.profile.precision);
        var fragmentSource = controller.profile.fragmentSource.replace(
          /__SA_FRAGMENT_PRECISION__/g,
          precision
        );
        vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SOURCE);
        fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
        program = gl.createProgram();
        if (!program) throw new Error('Unable to allocate program');
        gl.attachShader(program, vertex);
        gl.attachShader(program, fragment);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
          throw new Error(gl.getProgramInfoLog(program) || 'Program linking failed');
        }

        var position = gl.getAttribLocation(program, 'a_position');
        var resolution = gl.getUniformLocation(program, 'u_resolution');
        var time = gl.getUniformLocation(program, 'u_time');
        if (position < 0 || resolution === null || time === null) {
          throw new Error('Profile does not implement Hero Visual ABI v1');
        }
        buffer = gl.createBuffer();
        if (!buffer) throw new Error('Unable to allocate buffer');
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
          gl.STATIC_DRAW
        );

        controller.gl = gl;
        controller.resources = {
          program: program,
          buffer: buffer,
          position: position,
          resolution: resolution,
          time: time,
          pointer: gl.getUniformLocation(program, 'u_pointer'),
          pointerStrength: gl.getUniformLocation(program, 'u_pointer_strength')
        };
        controller.pointer = controller.resources.pointer !== null &&
          controller.resources.pointerStrength !== null
          ? { x: 0, y: 0, strength: 1, targetStrength: 1 }
          : null;
        controller.failed = false;
        attachPointer(controller);
      } catch (error) {
        if (buffer) gl.deleteBuffer(buffer);
        if (program) gl.deleteProgram(program);
        controller.failed = true;
        setState(controller.canvas, 'fallback');
        warn(controller, 'profile rejected', error && error.message);
      } finally {
        if (vertex) gl.deleteShader(vertex);
        if (fragment) gl.deleteShader(fragment);
      }
      return !!controller.resources;
    }

    function updateRect(controller) {
      var rect = controller.canvas.getBoundingClientRect();
      var changed = !controller.rect || controller.rect.width !== rect.width ||
        controller.rect.height !== rect.height;
      controller.rect = rect;
      controller.hasSize = !!(rect && rect.width > 0 && rect.height > 0);
      return changed;
    }

    function eligible(controller) {
      return !destroyed && !controller.destroyed && !controller.failed && !controller.lost &&
        !hasSaveData() && !suspendedForBfcache && !doc.hidden && !introActive() &&
        controller.canvas.isConnected !== false && controller.hasSize && controller.inView;
    }

    function draw(controller, seconds) {
      if (!initialize(controller)) return false;
      try {
        updateRect(controller);
        if (!controller.hasSize) return false;
        var gl = controller.gl;
        var resources = controller.resources;
        var requestedDpr = Number(win.devicePixelRatio) || 1;
        var dpr = controller.profile.dprCap === null
          ? requestedDpr
          : Math.min(requestedDpr, controller.profile.dprCap);
        var width = Math.max(1, Math.floor(controller.rect.width * dpr));
        var height = Math.max(1, Math.floor(controller.rect.height * dpr));
        if (gl.getParameter && gl.MAX_VIEWPORT_DIMS !== undefined) {
          var maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
          if (maxViewport && maxViewport[0] > 0 && maxViewport[1] > 0) {
            var viewportScale = Math.min(1, maxViewport[0] / width, maxViewport[1] / height);
            width = Math.max(1, Math.floor(width * viewportScale));
            height = Math.max(1, Math.floor(height * viewportScale));
          }
        }
        if (controller.canvas.width !== width) controller.canvas.width = width;
        if (controller.canvas.height !== height) controller.canvas.height = height;

        gl.viewport(0, 0, width, height);
        gl.useProgram(resources.program);
        gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
        gl.enableVertexAttribArray(resources.position);
        gl.vertexAttribPointer(resources.position, 2, gl.FLOAT, false, 0, 0);
        gl.uniform2f(resources.resolution, width, height);
        gl.uniform1f(resources.time, seconds);
        if (resources.pointer !== null && resources.pointerStrength !== null && controller.pointer) {
          controller.pointer.strength +=
            (controller.pointer.targetStrength - controller.pointer.strength) * 0.06;
          controller.pointer.targetStrength += (1 - controller.pointer.targetStrength) * 0.04;
          gl.uniform2f(resources.pointer, controller.pointer.x * width, controller.pointer.y * height);
          gl.uniform1f(resources.pointerStrength, controller.pointer.strength);
        }
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        controller.staticPending = false;
        setState(controller.canvas, 'ready');
        return true;
      } catch (error) {
        releaseResources(controller, true);
        controller.failed = true;
        setState(controller.canvas, 'fallback');
        warn(controller, 'draw failed', error && error.message);
        return false;
      }
    }

    function needsFrame() {
      var needed = false;
      controllers.forEach(function (controller) {
        if (controller.profile.engine !== FRAGMENT_ENGINE) return;
        if (eligible(controller) && (!reduceMotion.matches || controller.staticPending)) needed = true;
      });
      return needed;
    }

    function schedule() {
      refreshExternalControllers();
      if (rafId || !needsFrame()) return;
      rafId = win.requestAnimationFrame(tick);
    }

    function cancelIfIdle() {
      if (rafId && !needsFrame()) {
        win.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }

    function tick(now) {
      rafId = 0;
      controllers.forEach(function (controller) {
        if (!eligible(controller)) return;
        if (reduceMotion.matches) {
          if (controller.staticPending) draw(controller, controller.profile.staticTime);
          return;
        }
        var fps = narrowViewport.matches ? controller.profile.fpsNarrow : controller.profile.fpsWide;
        var interval = 1000 / fps;
        if (controller.lastFrame === null || now - controller.lastFrame >= interval) {
          if (draw(controller, now * 0.001)) controller.lastFrame = now;
        }
      });
      schedule();
    }

    function destroyController(controller) {
      if (!controller || controller.destroyed) return;
      controller.destroyed = true;
      if (controller.resizeObserver) controller.resizeObserver.disconnect();
      if (controller.intersectionObserver) controller.intersectionObserver.disconnect();
      controller.canvas.removeEventListener('webglcontextlost', controller.onContextLost);
      controller.canvas.removeEventListener('webglcontextrestored', controller.onContextRestored);
      releaseResources(controller, true);
      controllers.delete(controller);
      byCanvas.delete(controller.canvas);
      setState(controller.canvas, 'destroyed');
      cancelIfIdle();
    }

    function mount(canvas) {
      if (!canvas || byCanvas.has(canvas) || destroyed) return;
      var profile = readProfile(canvas);
      if (!profile) {
        setState(canvas, 'fallback');
        return;
      }
      var controller = {
        canvas: canvas,
        profileId: profile.id,
        profile: profile,
        resources: null,
        gl: null,
        rect: null,
        hasSize: false,
        inView: true,
        staticPending: true,
        lastFrame: null,
        pointer: null,
        pointerTarget: null,
        externalApp: null,
        externalLoading: false,
        externalGeneration: 0,
        externalClickTarget: null,
        onExternalClick: null,
        failed: false,
        lost: false,
        destroyed: false,
        warned: {}
      };
      byCanvas.set(canvas, controller);
      controllers.add(controller);
      updateRect(controller);

      controller.onContextLost = function (event) {
        if (event && event.preventDefault) event.preventDefault();
        controller.lost = true;
        releaseResources(controller, false);
        setState(canvas, 'fallback');
        cancelIfIdle();
      };
      controller.onContextRestored = function () {
        controller.lost = false;
        controller.failed = false;
        controller.staticPending = true;
        schedule();
      };
      canvas.addEventListener('webglcontextlost', controller.onContextLost);
      canvas.addEventListener('webglcontextrestored', controller.onContextRestored);

      if (win.ResizeObserver) {
        controller.resizeObserver = new win.ResizeObserver(function () {
          if (controller.destroyed || !updateRect(controller)) return;
          controller.staticPending = true;
          schedule();
          cancelIfIdle();
        });
        controller.resizeObserver.observe(canvas);
      }
      if (win.IntersectionObserver) {
        controller.intersectionObserver = new win.IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i += 1) {
            if (entries[i].target !== canvas) continue;
            controller.inView = entries[i].isIntersecting !== false && entries[i].intersectionRatio !== 0;
          }
          schedule();
          cancelIfIdle();
        }, { rootMargin: '120px 0px' });
        controller.intersectionObserver.observe(canvas);
      }

      if (hasSaveData()) setState(canvas, 'fallback');
      else schedule();
    }

    function scan(scope) {
      if (!scope || destroyed) return;
      if (scope.matches && scope.matches(CANVAS_SELECTOR)) mount(scope);
      if (!scope.querySelectorAll) return;
      var canvases = scope.querySelectorAll(CANVAS_SELECTOR);
      for (var i = 0; i < canvases.length; i += 1) mount(canvases[i]);
    }

    function onMotionChange() {
      controllers.forEach(function (controller) {
        controller.staticPending = true;
        controller.lastFrame = null;
        if (reduceMotion.matches) {
          if (controller.pointer) {
            controller.pointer = { x: 0, y: 0, strength: 1, targetStrength: 1 };
          }
          detachPointer(controller);
        }
        else attachPointer(controller);
      });
      schedule();
      cancelIfIdle();
    }

    function onPointerChange() {
      controllers.forEach(function (controller) {
        if (controller.profile.engine !== FRAGMENT_ENGINE) return;
        if (finePointer.matches) attachPointer(controller);
        else {
          if (controller.pointer) {
            controller.pointer = { x: 0, y: 0, strength: 1, targetStrength: 1 };
          }
          detachPointer(controller);
        }
      });
    }

    function onSaveDataChange() {
      controllers.forEach(function (controller) {
        if (hasSaveData()) {
          releaseResources(controller, true);
          setState(controller.canvas, 'fallback');
        } else {
          controller.failed = false;
          controller.staticPending = true;
          setState(controller.canvas, 'idle');
        }
      });
      schedule();
      cancelIfIdle();
    }

    function onSectionLoad(event) {
      scan(event.target);
      schedule();
    }

    function onSectionUnload(event) {
      var scope = event.target;
      Array.from(controllers).forEach(function (controller) {
        if (scope === controller.canvas || (scope && scope.contains && scope.contains(controller.canvas))) {
          destroyController(controller);
        }
      });
    }

    function onVisibilityChange() {
      schedule();
      cancelIfIdle();
    }

    function onPageHide(event) {
      if (event.persisted) {
        suspendedForBfcache = true;
        refreshExternalControllers();
        cancelIfIdle();
      } else {
        destroy();
      }
    }

    function onPageShow(event) {
      if (!event.persisted || destroyed) return;
      suspendedForBfcache = false;
      controllers.forEach(function (controller) {
        updateRect(controller);
        controller.staticPending = true;
      });
      schedule();
    }

    function installListeners() {
      listen(doc, 'shopify:section:load', onSectionLoad);
      listen(doc, 'shopify:section:unload', onSectionUnload);
      listen(doc, 'visibilitychange', onVisibilityChange);
      listen(doc, 'sa-hintro:done', schedule);
      listen(win, 'pagehide', onPageHide);
      listen(win, 'pageshow', onPageShow);
      listen(win, 'load', schedule);
      listen(win, 'resize', function () {
        controllers.forEach(function (controller) {
          if (updateRect(controller)) controller.staticPending = true;
        });
        schedule();
      });
      listen(reduceMotion, 'change', onMotionChange);
      listen(narrowViewport, 'change', schedule);
      listen(finePointer, 'change', onPointerChange);
      listen(connection, 'change', onSaveDataChange);
    }

    function start() {
      if (started || destroyed) return;
      started = true;
      installListeners();
      if (doc.readyState === 'loading') {
        var onReady = function () {
          doc.removeEventListener('DOMContentLoaded', onReady);
          scan(doc);
        };
        listen(doc, 'DOMContentLoaded', onReady);
      } else {
        scan(doc);
      }
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      if (rafId) {
        win.cancelAnimationFrame(rafId);
        rafId = 0;
      }
      Array.from(controllers).forEach(destroyController);
      while (listeners.length) listeners.pop()();
    }

    return {
      start: start,
      destroy: destroy
    };
  }

  return { createHeroVisualManager: createHeroVisualManager };
});
